import _ from "lodash";
import React, { Fragment } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Frequency } from "@sportal/api";

import { withLayout } from "../../components/layout/Layout";
import { withPromptDialog } from "../../hocs/withPrompt";
import { makeComparator } from "../../utils/makeComparator";
import { Form, FormSubmitButton } from "../../components/form";
import { Input } from "../../components/input/Input";
import { ReportFrequencyPicker } from "./components/ReportFrequencyPicker";
import {
  changeAddress,
  changeFrequency,
  discardScheduledReportsSettings,
  saveScheduledReportsSettings,
} from "../../store/attributes/attributes.actions";
import { Button } from "../../components/button/Button";
import { getAccountSettings } from "../../store/root.selectors";
import { useIntlTranslate } from "../../hooks";
import { AriaPageTitle } from "../../components/ariaPageTitle";
import { Desktop } from "../../hocs/Responsive";
import { withPageName } from "../../components/layout/horizontalMenu/PageName";

import "./ScheduledReportsPage.scss";

const ScheduledReportsPageComponent = withPromptDialog(
  ({
    hasChanges,
    save,
    discard,
    onChangeAddress,
    onChangeFrequency,
    address,
    frequency,
    isValid,
  }) => {
    const translate = useIntlTranslate();

    return (
      <section className="scheduled-reports row">
        <div className="col-12 col-lg-8 scheduled-reports__wrapper">
          <Desktop>
            <h1 className="scheduled-reports__header">
              <FormattedMessage id={"scheduled_reports"} />
            </h1>
          </Desktop>

          <Form
            onSubmit={save}
            name={"scheduledReportsForm"}
            description={<FormattedMessage id={"manage_report_settings"} />}
          >
            <div className="scheduled-reports-form row">
              <div
                className="col-12 col-md-7 col-lg-6"
                data-testid="input-container"
              >
                <Input
                  id="scheduled-reports-email-input"
                  className="scheduled-reports-form__mail"
                  onChange={({ target: { value } }) => onChangeAddress(value)}
                  onClear={() => onChangeAddress("")}
                  error={!isValid && <FormattedMessage id="email_error" />}
                  value={address}
                  label={<FormattedMessage id="email" />}
                  placeholder={translate("email_placeholder")}
                />
              </div>

              <div className="col-12 col-md-5 col-lg-4">
                <ReportFrequencyPicker
                  data-testid="report-frequency-picker"
                  selected={frequency}
                  onChange={onChangeFrequency}
                />
              </div>
            </div>
            <Button
              data-testid="discard-button"
              color={"secondary"}
              size={"large"}
              onClick={discard}
              disabled={!hasChanges}
            >
              <FormattedMessage id="discard" />
            </Button>

            <FormSubmitButton
              label={<FormattedMessage id="save" />}
              disabled={!isValid || !hasChanges}
            />
          </Form>
        </div>
      </section>
    );
  }
);

const getScheduledReportsSettings = ({ email: { address, frequency } }) => ({
  address,
  frequency,
});

const comparator = makeComparator(
  ({ toSave }) => getScheduledReportsSettings(toSave),
  getScheduledReportsSettings
);

const validateAddress = (address) =>
  /^([^\s"@]+|(?:"[^"]+"))@([^.\s@]+\.)+[^.\s@]{2,63}$/.test(address);

export const ScheduledReportsPageContent = () => {
  const dispatch = useDispatch();
  const accountSettings = useSelector(getAccountSettings);
  const hasChanges = !comparator(accountSettings);
  const save = () => dispatch(saveScheduledReportsSettings());
  const discard = () => dispatch(discardScheduledReportsSettings());
  const { address, frequency } = getScheduledReportsSettings(
    accountSettings.toSave
  );
  const isValid =
    frequency === Frequency.Never
      ? _.isEmpty(address) || validateAddress(address)
      : !_.isEmpty(address) && validateAddress(address);

  const handleOnChangeAddress = (value) => {
    dispatch(changeAddress(value));
  };

  const handleOnChangeFrequency = (value) => {
    dispatch(changeFrequency(value));
  };

  return (
    <Fragment>
      <AriaPageTitle pageKey="page_title_scheduled_reports_page" />
      <ScheduledReportsPageComponent
        hasChanges={hasChanges}
        save={save}
        discard={discard}
        accountSettings={accountSettings}
        onChangeAddress={handleOnChangeAddress}
        onChangeFrequency={handleOnChangeFrequency}
        address={address}
        frequency={frequency}
        isValid={isValid}
      />
    </Fragment>
  );
};

export const ScheduledReportsPage = _.flowRight(
  withLayout(),
  withPageName()
)(ScheduledReportsPageContent);
